import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
    User, Building2, Mail, Phone, Globe, Linkedin, Calendar, MapPin,
    LayoutTemplate, AlignVerticalJustifyStart,
    PanelLeftClose, PanelRightClose, RectangleHorizontal, Circle, Square,
    Check, ChevronDown, ChevronRight, Settings2, GripVertical, Type, Code, FileText,
    Eye, Users, Search, X
} from 'lucide-react';
import DOMPurify from 'dompurify';
import ContentPicker from './ContentPicker';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { signatureAssetMergeMap } from '../lib/signatureAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20";
const LABEL = "block text-[11px] font-medium text-stone-500 mb-1";

const FIELD_DEFS = [
    { key: 'name', label: 'Full Name', icon: User, mergeVar: '{{SenderName}}', placeholder: 'John Smith', defaultEnabled: true },
    { key: 'title', label: 'Title', icon: Building2, mergeVar: '{{SenderTitle}}', placeholder: 'Sales Manager', defaultEnabled: true },
    { key: 'company', label: 'Company', icon: Building2, mergeVar: '{{SenderCompany}}', placeholder: 'Acme Inc.', defaultEnabled: true },
    { key: 'email', label: 'Email', icon: Mail, mergeVar: '{{SenderEmail}}', placeholder: 'john@acme.com', defaultEnabled: true },
    { key: 'phone', label: 'Phone', icon: Phone, mergeVar: '{{SenderPhone}}', placeholder: '+1 555 123 4567', defaultEnabled: true },
    { key: 'website', label: 'Website', icon: Globe, mergeVar: null, placeholder: 'acme.com', defaultEnabled: false },
    { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, mergeVar: '{{SenderLinkedIn}}', placeholder: 'linkedin.com/in/johnsmith', defaultEnabled: false },
    { key: 'calendar_link', label: 'Booking Link', icon: Calendar, mergeVar: null, placeholder: 'cal.com/john', defaultEnabled: false },
    { key: 'address', label: 'Address', icon: MapPin, mergeVar: '{{SenderAddress}}', placeholder: '123 Main St, City', defaultEnabled: false },
    { key: 'pronouns', label: 'Pronouns', icon: User, mergeVar: null, placeholder: 'he/him', defaultEnabled: false },
    { key: 'tagline', label: 'Tagline', icon: Building2, mergeVar: null, placeholder: 'Helping teams sell smarter', defaultEnabled: false },
];

const SIGN_OFF_OPTIONS = [
    'Best regards,',
    'Kind regards,',
    'Regards,',
    'Warm regards,',
    'Best,',
    'Thanks,',
    'Thank you,',
    'Cheers,',
    'All the best,',
    'Sincerely,',
];

const DEFAULT_FIELD_ORDER = FIELD_DEFS.map(f => f.key);

const LAYOUTS = [
    { id: 'photo-left', label: 'Photo Left', icon: PanelLeftClose },
    { id: 'photo-right', label: 'Photo Right', icon: PanelRightClose },
    { id: 'stacked', label: 'Stacked', icon: AlignVerticalJustifyStart },
    { id: 'banner', label: 'Banner', icon: RectangleHorizontal },
];

const PHOTO_SHAPES = [
    { id: 'circle', label: 'Circle', icon: Circle },
    { id: 'rounded', label: 'Rounded', icon: RectangleHorizontal },
    { id: 'square', label: 'Square', icon: Square },
];

const PRESETS = [
    { id: 'professional', label: 'Professional', accent: '#1c1917', text: '#44403c', divider: '#e7e5e4', nameSize: '16' },
    { id: 'modern', label: 'Modern', accent: '#2563eb', text: '#475569', divider: '#3b82f6', nameSize: '18' },
    { id: 'warm', label: 'Warm', accent: '#b45309', text: '#44403c', divider: '#f59e0b', nameSize: '16' },
    { id: 'minimal', label: 'Minimal', accent: '#525252', text: '#737373', divider: '#e5e5e5', nameSize: '14' },
    { id: 'bold', label: 'Bold', accent: '#dc2626', text: '#1c1917', divider: '#dc2626', nameSize: '20' },
];

// -------------------------------------------------------------------
// Email-safe HTML builder (client-side, used for preview + export)
// Uses HTML tables + inline styles for maximum Gmail/Outlook compat
// -------------------------------------------------------------------
function esc(t) { return (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

function buildSignatureHtml({ layout, fields, enabledFields, fieldOrder, style, photoUrl, logoUrl, photoShape, signOff }) {
    const accent = style.accent_color || '#1c1917';
    const textColor = style.text_color || '#44403c';
    const nameSize = style.name_size || '16';
    const font = style.font_family || 'Arial, Helvetica, sans-serif';
    const dividerColor = style.divider_color || '#e7e5e4';
    const photoSize = style.photo_size || '80';

    // Only include enabled fields, in the specified order
    const orderedFields = (fieldOrder || DEFAULT_FIELD_ORDER).filter(k => enabledFields[k] && fields[k]);

    // Build name line
    const name = enabledFields.name ? esc(fields.name || '') : '';
    const pronouns = enabledFields.pronouns ? esc(fields.pronouns || '') : '';
    const title = enabledFields.title ? esc(fields.title || '') : '';
    const company = enabledFields.company ? esc(fields.company || '') : '';
    const tagline = enabledFields.tagline ? esc(fields.tagline || '') : '';
    const email = enabledFields.email ? esc(fields.email || '') : '';
    const phone = enabledFields.phone ? esc(fields.phone || '') : '';
    const website = enabledFields.website ? esc(fields.website || '') : '';
    const linkedin = enabledFields.linkedin ? esc(fields.linkedin || '') : '';
    const calLink = enabledFields.calendar_link ? esc(fields.calendar_link || '') : '';
    const address = enabledFields.address ? esc(fields.address || '') : '';

    // Name row
    let nameHtml = '';
    if (name) {
        nameHtml = `<tr><td style="padding:0;font-size:${nameSize}px;font-weight:700;color:${accent};font-family:${font};line-height:1.3">${name}`;
        if (pronouns) nameHtml += ` <span style="font-size:11px;color:#a8a29e;font-weight:400">(${pronouns})</span>`;
        nameHtml += '</td></tr>';
    }

    // Title row
    const titleHtml = title
        ? `<tr><td style="padding:2px 0 0 0;font-size:13px;color:${textColor};font-family:${font};line-height:1.4">${title}</td></tr>`
        : '';

    // Company row
    const companyHtml = company
        ? `<tr><td style="padding:1px 0 0 0;font-size:13px;color:${textColor};font-family:${font};line-height:1.4">${company}</td></tr>`
        : '';

    // Tagline row
    const taglineHtml = tagline
        ? `<tr><td style="padding:2px 0 0 0;font-size:12px;color:#78716c;font-style:italic;font-family:${font};line-height:1.4">${tagline}</td></tr>`
        : '';

    // Divider
    const divider = `<tr><td style="padding:8px 0"><table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="border-top:2px solid ${dividerColor};font-size:1px;line-height:1px" height="1">&nbsp;</td></tr></table></td></tr>`;

    // Contact info rows (each on its own line for Outlook)
    const contactRows = [];
    if (email) contactRows.push(`<a href="mailto:${email}" style="color:${accent};text-decoration:none;font-family:${font}">${email}</a>`);
    if (phone) contactRows.push(`<a href="tel:${phone}" style="color:${textColor};text-decoration:none;font-family:${font}">${phone}</a>`);
    if (website) {
        const href = website.startsWith('http') ? website : `https://${website}`;
        contactRows.push(`<a href="${esc(href)}" style="color:${accent};text-decoration:none;font-family:${font}" target="_blank">${website}</a>`);
    }
    const contactHtml = contactRows.length
        ? `<tr><td style="padding:2px 0 0 0;font-size:12px;line-height:1.8;font-family:${font}">${contactRows.join(' &#8226; ')}</td></tr>`
        : '';

    // Social links
    const socialParts = [];
    if (linkedin) {
        const href = linkedin.startsWith('http') ? linkedin : `https://linkedin.com/in/${linkedin}`;
        socialParts.push(`<a href="${esc(href)}" style="color:${accent};text-decoration:none;font-size:12px;font-family:${font}" target="_blank">LinkedIn</a>`);
    }
    if (calLink) {
        const href = calLink.startsWith('http') ? calLink : `https://${calLink}`;
        socialParts.push(`<a href="${esc(href)}" style="color:${accent};text-decoration:none;font-size:12px;font-family:${font}" target="_blank">Book a Meeting</a>`);
    }
    const socialHtml = socialParts.length
        ? `<tr><td style="padding:4px 0 0 0;font-size:12px;font-family:${font}">${socialParts.join(' &middot; ')}</td></tr>`
        : '';

    // Address
    const addressHtml = address
        ? `<tr><td style="padding:4px 0 0 0;font-size:11px;color:#a8a29e;font-family:${font}">${address}</td></tr>`
        : '';

    // Logo
    const logoRow = logoUrl
        ? `<tr><td style="padding:8px 0 0 0"><img src="${esc(logoUrl)}" alt="${esc(company)}" style="max-height:32px;max-width:120px;display:block" width="120" /></td></tr>`
        : '';

    // Text block table
    const textTable = `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="font-family:${font}">${nameHtml}${titleHtml}${companyHtml}${taglineHtml}${(nameHtml || titleHtml || companyHtml) && (contactHtml || socialHtml) ? divider : ''}${contactHtml}${socialHtml}${addressHtml}${logoRow}</table>`;

    // Sign-off block (above signature)
    const signOffHtml = signOff
        ? `<p style="font-size:14px;color:${textColor};font-family:${font};margin:0 0 12px 0;line-height:1.4">${esc(signOff)}</p>`
        : '';

    // Photo cell
    const borderRadius = photoShape === 'circle' ? '50%' : (photoShape === 'rounded' ? '8px' : '0');
    const photoImg = photoUrl
        ? `<img src="${esc(photoUrl)}" alt="${esc(name)}" width="${photoSize}" height="${photoSize}" style="border-radius:${borderRadius};display:block;object-fit:cover" />`
        : '';

    // Wrap in outer table with Outlook-compatible structure
    // Using <!--[if mso]> conditional comments for Outlook width fix
    let inner;
    if ((layout === 'photo-left' || layout === 'photo-right') && photoImg) {
        const photoTd = `<td style="vertical-align:top;padding:0;width:${photoSize}px" width="${photoSize}" valign="top">${photoImg}</td>`;
        const spacerTd = `<td style="width:16px;font-size:1px" width="16">&nbsp;</td>`;
        const textTd = `<td style="vertical-align:top;padding:0" valign="top">${textTable}</td>`;
        const cells = layout === 'photo-left'
            ? `${photoTd}${spacerTd}${textTd}`
            : `${textTd}${spacerTd}${photoTd}`;
        inner = `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr>${cells}</tr></table>`;
    } else if (layout === 'banner' && photoImg) {
        inner = `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr><td style="padding:0 0 12px 0;text-align:center" align="center"><img src="${esc(photoUrl)}" alt="${esc(name)}" width="500" style="border-radius:8px;max-width:100%;height:auto;display:block" /></td></tr><tr><td style="text-align:center" align="center">${textTable}</td></tr></table>`;
    } else {
        const photoRow = photoImg ? `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr><td style="padding:0 0 10px 0">${photoImg}</td></tr></table>` : '';
        inner = `${photoRow}${textTable}`;
    }

    // Outer wrapper — max-width via Outlook conditional comment
    return `<!--[if mso]><table cellpadding="0" cellspacing="0" border="0" width="500"><tr><td><![endif]--><div style="max-width:500px;font-family:${font}">${signOffHtml}${inner}</div><!--[if mso]></td></tr></table><![endif]-->`;
}

function buildSignatureText({ fields, enabledFields, fieldOrder, signOff }) {
    const order = fieldOrder || DEFAULT_FIELD_ORDER;
    const lines = [];
    if (signOff) { lines.push(signOff); lines.push(''); }
    const name = enabledFields.name ? fields.name : '';
    const pronouns = enabledFields.pronouns ? fields.pronouns : '';
    const title = enabledFields.title ? fields.title : '';
    const company = enabledFields.company ? fields.company : '';
    const tagline = enabledFields.tagline ? fields.tagline : '';
    const email = enabledFields.email ? fields.email : '';
    const phone = enabledFields.phone ? fields.phone : '';
    const website = enabledFields.website ? fields.website : '';
    const linkedin = enabledFields.linkedin ? fields.linkedin : '';
    const calLink = enabledFields.calendar_link ? fields.calendar_link : '';
    const address = enabledFields.address ? fields.address : '';

    if (name) lines.push(name + (pronouns ? ` (${pronouns})` : ''));
    if (title) lines.push(title);
    if (company) lines.push(company);
    if (tagline) lines.push(tagline);
    if (lines.length && (email || phone || website)) lines.push('---');
    if (email) lines.push(email);
    if (phone) lines.push(phone);
    if (website) lines.push(website);
    const social = [];
    if (linkedin) social.push(`LinkedIn: ${linkedin.startsWith('http') ? linkedin : `https://linkedin.com/in/${linkedin}`}`);
    if (calLink) social.push(`Book a meeting: ${calLink.startsWith('http') ? calLink : `https://${calLink}`}`);
    social.forEach(s => lines.push(s));
    if (address) lines.push(address);
    return lines.join('\n');
}

// -------------------------------------------------------------------
// Searchable User Preview Dropdown
// -------------------------------------------------------------------
function UserPreviewSearch({ users, selectedId, selectedUser, onSelect }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        function handleClick(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Focus input when opened
    useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

    const filtered = useMemo(() => {
        if (!search.trim()) return users;
        const q = search.toLowerCase();
        return users.filter(u => {
            const name = (u.name || '').toLowerCase();
            const email = (u.email || '').toLowerCase();
            return name.includes(q) || email.includes(q);
        });
    }, [users, search]);

    const displayLabel = selectedUser ? (selectedUser.name || selectedUser.email) : 'Template view';

    return (
        <div className="relative" ref={wrapRef}>
            {/* Trigger button */}
            <button
                type="button"
                onClick={() => { setOpen(!open); setSearch(''); }}
                className="flex items-center gap-1.5 text-xs border border-stone-200 rounded-lg pl-7 pr-2 py-1.5 bg-white text-stone-700 hover:border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-400 cursor-pointer max-w-[220px] truncate"
            >
                <span className="truncate">{displayLabel}</span>
                <ChevronDown className="w-3 h-3 text-stone-400 shrink-0" />
            </button>
            <Eye className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400 pointer-events-none" />

            {/* Dropdown */}
            {open && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-stone-200 rounded-lg shadow-lg z-50 overflow-hidden">
                    {/* Search input */}
                    <div className="p-2 border-b border-stone-100">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name or email..."
                                className="w-full pl-7 pr-7 py-1.5 text-xs border border-stone-200 rounded-md bg-stone-50 focus:outline-none focus:ring-1 focus:ring-stone-400"
                            />
                            {search && (
                                <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <X className="w-3 h-3 text-stone-400 hover:text-stone-600" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Options list */}
                    <div className="max-h-60 overflow-y-auto">
                        {/* Template view option */}
                        <button
                            type="button"
                            onClick={() => { onSelect(''); setOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-stone-50 flex items-center gap-2 ${!selectedId ? 'bg-stone-50 font-medium text-stone-900' : 'text-stone-600'}`}
                        >
                            {!selectedId && <Check className="w-3 h-3 text-stone-700" />}
                            {selectedId && <span className="w-3" />}
                            Template view
                        </button>

                        {filtered.length === 0 && (
                            <p className="px-3 py-3 text-xs text-stone-400 text-center">No users found</p>
                        )}

                        {filtered.map(u => {
                            const isSelected = u.user_id === selectedId;
                            return (
                                <button
                                    key={u.user_id}
                                    type="button"
                                    onClick={() => { onSelect(u.user_id); setOpen(false); }}
                                    className={`w-full text-left px-3 py-2 text-xs hover:bg-stone-50 flex items-center gap-2 ${isSelected ? 'bg-stone-50 font-medium text-stone-900' : 'text-stone-600'}`}
                                >
                                    {isSelected && <Check className="w-3 h-3 text-stone-700" />}
                                    {!isSelected && <span className="w-3" />}
                                    <span className="truncate">{u.name || u.email}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// -------------------------------------------------------------------
// Main Component
// -------------------------------------------------------------------
export default function SignatureEditor({ value, onChange, signatureData: initData, onDataChange, isAdmin = false }) {
    const { user, getAuthHeader } = useAuth();

    const [layout, setLayout] = useState('photo-left');
    const [photoShape, setPhotoShape] = useState('circle');
    const [fieldOrder, setFieldOrder] = useState(DEFAULT_FIELD_ORDER);
    const [enabledFields, setEnabledFields] = useState(() => {
        const defaults = {};
        FIELD_DEFS.forEach(f => { defaults[f.key] = f.defaultEnabled; });
        return defaults;
    });
    const [fields, setFields] = useState({
        name: '', title: '', company: '', email: '', phone: '',
        website: '', linkedin: '', address: '', pronouns: '',
        calendar_link: '', tagline: '',
    });
    const [style, setStyle] = useState({
        accent_color: '#1c1917', text_color: '#44403c', divider_color: '#e7e5e4',
        name_size: '16', font_family: 'Arial, Helvetica, sans-serif', photo_size: '80',
    });
    const [signOff, setSignOff] = useState('Best regards,');
    const [photoUrl, setPhotoUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [viewMode, setViewMode] = useState('visual'); // 'visual', 'html', 'text'
    const [useProfilePhoto, setUseProfilePhoto] = useState(false);
    const [useCompanyLogo, setUseCompanyLogo] = useState(false);
    const [showLogoPicker, setShowLogoPicker] = useState(false);
    const [showLayoutStyle, setShowLayoutStyle] = useState(true);
    const [showPhotos, setShowPhotos] = useState(true);
    const [showFields, setShowFields] = useState(true);

    // Preview user selection (admin mode)
    const [previewUsers, setPreviewUsers] = useState([]);
    const [previewUserId, setPreviewUserId] = useState('');
    const [previewUser, setPreviewUser] = useState(null);
    // Fetch org users for preview selector (admin mode only)
    useEffect(() => {
        if (!isAdmin) return;
        (async () => {
            try {
                const res = await axios.get(`${API}/admin/users`, { headers: getAuthHeader() });
                const users = res.data?.users || res.data || [];
                users.sort((a, b) => {
                    const nameA = (a.name || a.email || '').toLowerCase();
                    const nameB = (b.name || b.email || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                setPreviewUsers(users);
            } catch { /* ignore */ }
        })();
    }, [isAdmin, getAuthHeader]);

    // DnD
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    // Initialize from existing data
    useEffect(() => {
        if (initData && Object.keys(initData).length > 0) {
            if (initData.layout) setLayout(initData.layout);
            if (initData.photo_shape) setPhotoShape(initData.photo_shape);
            if (initData.field_order) setFieldOrder(initData.field_order);
            if (initData.fields) {
                setFields(prev => ({ ...prev, ...initData.fields }));
                if (initData.enabled_fields) {
                    setEnabledFields(initData.enabled_fields);
                } else {
                    const enabled = {};
                    FIELD_DEFS.forEach(f => { enabled[f.key] = !!initData.fields[f.key]; });
                    setEnabledFields(enabled);
                }
            }
            if (initData.style) setStyle(prev => ({ ...prev, ...initData.style }));
            if (initData.sign_off !== undefined) setSignOff(initData.sign_off);
            if (initData.photo_url) {
                setPhotoUrl(initData.photo_url);
                if (initData.photo_url === '{{SenderPhoto}}') setUseProfilePhoto(true);
            }
            if (initData.logo_url) {
                setLogoUrl(initData.logo_url);
                if (initData.logo_url === '{{CompanyLogo}}') setUseCompanyLogo(true);
            }
        } else if (value && !initData) {
            setViewMode('html');
        }
    }, []);

    // Prefill from user profile or admin merge vars
    useEffect(() => {
        if (!initData && !value && user && !isAdmin) {
            setFields(prev => ({
                ...prev,
                name: user.name || '',
                title: user.title || '',
                email: user.email || '',
                phone: user.phone || '',
                company: user.company_name || '',
                website: user.personal_website || user.company_website || '',
                linkedin: user.linkedin_url || '',
                pronouns: user.pronouns || '',
            }));
            if (user.picture) setPhotoUrl(user.picture);
            if (user.company_logo_url) setLogoUrl(user.company_logo_url);
        }
        if (!initData && !value && isAdmin) {
            setFields({
                name: '{{SenderName}}',
                title: '{{SenderTitle}}',
                company: '{{SenderCompany}}',
                email: '{{SenderEmail}}',
                phone: '{{SenderPhone}}',
                website: '',
                linkedin: '{{SenderLinkedIn}}',
                address: '',
                pronouns: '',
                calendar_link: '',
                tagline: '',
            });
        }
    }, [user]);

    // Build HTML + text whenever anything changes
    const htmlOutput = useMemo(() =>
        buildSignatureHtml({ layout, fields, enabledFields, fieldOrder, style, photoUrl, logoUrl, photoShape, signOff }),
        [layout, fields, enabledFields, fieldOrder, style, photoUrl, logoUrl, photoShape, signOff]
    );

    const textOutput = useMemo(() =>
        buildSignatureText({ fields, enabledFields, fieldOrder, signOff }),
        [fields, enabledFields, fieldOrder, signOff]
    );

    // When preview user changes, build resolved preview
    const previewHtmlOutput = useMemo(() => {
        if (!isAdmin || !previewUser) return null;
        let html = htmlOutput || '';
        // v2.16.38: render the proxy URL for photo/logo so the
        // admin preview matches what recipients of *that* user's
        // outgoing emails will actually see.
        const replacements = {
            '{{SenderName}}': previewUser.name || `${previewUser.first_name || ''} ${previewUser.last_name || ''}`.trim() || previewUser.email || '',
            '{{SenderTitle}}': previewUser.title || previewUser.job_title || '',
            '{{SenderCompany}}': previewUser.company || previewUser.company_name || '',
            '{{SenderEmail}}': previewUser.email || '',
            '{{SenderPhone}}': previewUser.phone || '',
            '{{SenderLinkedIn}}': previewUser.linkedin_url || previewUser.linkedin || '',
            ...signatureAssetMergeMap(previewUser),
        };
        Object.entries(replacements).forEach(([key, val]) => {
            html = html.split(key).join(val || '');
        });
        return html;
    }, [isAdmin, previewUser, htmlOutput]);

    // Emit changes
    useEffect(() => {
        const data = {
            layout,
            photo_shape: photoShape,
            fields,
            style,
            photo_url: photoUrl,
            logo_url: logoUrl,
            enabled_fields: enabledFields,
            field_order: fieldOrder,
            sign_off: signOff,
        };
        onChange?.(htmlOutput);
        onDataChange?.(data);
    }, [htmlOutput]);

    const updateField = (key, val) => setFields(prev => ({ ...prev, [key]: val }));
    const updateStyle = (key, val) => setStyle(prev => ({ ...prev, [key]: val }));
    const toggleField = (key) => setEnabledFields(prev => ({ ...prev, [key]: !prev[key] }));

    const applyPreset = (preset) => {
        setStyle(prev => ({
            ...prev,
            accent_color: preset.accent,
            text_color: preset.text,
            divider_color: preset.divider,
            name_size: preset.nameSize,
        }));
    };

    // DnD handler for field reorder
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setFieldOrder(prev => {
                const oldIndex = prev.indexOf(active.id);
                const newIndex = prev.indexOf(over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    const enabledCount = Object.values(enabledFields).filter(Boolean).length;

    // Ordered field defs for rendering
    const orderedFieldDefs = fieldOrder.map(key => FIELD_DEFS.find(f => f.key === key)).filter(Boolean);

    return (
        <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
            {/* Header with view mode tabs */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-stone-50 border-b border-stone-200">
                <div className="flex items-center gap-2">
                    <LayoutTemplate className="w-4 h-4 text-stone-500" />
                    <span className="text-sm font-medium text-stone-700">Signature Builder</span>
                </div>
                <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-lg p-0.5">
                    {[
                        { id: 'visual', label: 'Visual', icon: LayoutTemplate },
                        { id: 'html', label: 'HTML', icon: Code },
                        { id: 'text', label: 'Text', icon: FileText },
                    ].map(mode => (
                        <button
                            key={mode.id}
                            onClick={() => setViewMode(mode.id)}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                                viewMode === mode.id
                                    ? 'bg-stone-900 text-white'
                                    : 'text-stone-500 hover:text-stone-700'
                            }`}
                        >
                            <mode.icon className="w-3 h-3" />
                            {mode.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* HTML view */}
            {viewMode === 'html' && (
                <div className="p-4 space-y-3">
                    <textarea
                        value={htmlOutput}
                        readOnly
                        className="w-full px-3 py-2 text-xs font-mono bg-stone-900 text-green-400 border border-stone-700 rounded-lg focus:outline-none"
                        rows={14}
                        onClick={(e) => e.target.select()}
                    />
                    <div className="border border-stone-200 rounded-lg p-4 bg-white">
                        <p className="text-[10px] text-stone-400 mb-2 uppercase tracking-wide font-medium">Preview</p>
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlOutput) }} />
                    </div>
                </div>
            )}

            {/* Text view */}
            {viewMode === 'text' && (
                <div className="p-4 space-y-3">
                    <textarea
                        value={textOutput}
                        readOnly
                        className="w-full px-3 py-2 text-sm font-mono bg-stone-50 text-stone-700 border border-stone-200 rounded-lg focus:outline-none"
                        rows={10}
                        onClick={(e) => e.target.select()}
                    />
                    <p className="text-[10px] text-stone-400">Plain text version for email clients that don't support HTML.</p>
                </div>
            )}

            {/* Visual builder */}
            {viewMode === 'visual' && (
                <div className="grid grid-cols-[320px_1fr] min-h-[480px]">
                    {/* Left: Controls */}
                    <div className="border-r border-stone-200 overflow-y-auto max-h-[600px]">

                        {/* Section 1: Layout & Style */}
                        <div className="border-b border-stone-200">
                            <button
                                onClick={() => setShowLayoutStyle(!showLayoutStyle)}
                                className="w-full flex items-center justify-between p-3 hover:bg-stone-50 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <Settings2 className="w-3.5 h-3.5 text-stone-400" />
                                    <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide">Layout & Style</span>
                                </div>
                                {showLayoutStyle ? <ChevronDown className="w-3.5 h-3.5 text-stone-400" /> : <ChevronRight className="w-3.5 h-3.5 text-stone-400" />}
                            </button>
                            {showLayoutStyle && (
                                <div className="px-3 pb-3 space-y-3">
                                    <div>
                                        <label className={LABEL}>Layout</label>
                                        <div className="grid grid-cols-2 gap-1.5">
                                            {LAYOUTS.map(l => (
                                                <button key={l.id} onClick={() => setLayout(l.id)}
                                                    className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs transition-all ${
                                                        layout === l.id
                                                            ? 'border-stone-900 bg-stone-50 text-stone-900 shadow-sm'
                                                            : 'border-stone-200 text-stone-500 hover:border-stone-300'
                                                    }`}>
                                                    <l.icon className="w-4 h-4" />
                                                    <span className="font-medium">{l.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className={LABEL}>Color Preset</label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {PRESETS.map(p => (
                                                <button key={p.id} onClick={() => applyPreset(p)}
                                                    className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] border border-stone-200 hover:border-stone-300 transition-colors">
                                                    <span className="w-2.5 h-2.5 rounded-full border border-stone-200" style={{ background: p.accent }} />
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2">
                                        <ColorInput label="Accent" value={style.accent_color} onChange={v => updateStyle('accent_color', v)} />
                                        <ColorInput label="Text" value={style.text_color} onChange={v => updateStyle('text_color', v)} />
                                        <ColorInput label="Divider" value={style.divider_color} onChange={v => updateStyle('divider_color', v)} />
                                    </div>

                                    <div>
                                        <label className={LABEL}>Name Size ({style.name_size}px)</label>
                                        <input type="range" min="12" max="24" step="1"
                                            value={style.name_size}
                                            onChange={e => updateStyle('name_size', e.target.value)}
                                            className="w-full accent-stone-900" />
                                    </div>
                                    <div>
                                        <label className={LABEL}>Font Family</label>
                                        <select value={style.font_family} onChange={e => updateStyle('font_family', e.target.value)} className={`${INPUT} text-xs`}>
                                            <option value="Arial, Helvetica, sans-serif">Arial</option>
                                            <option value="Georgia, 'Times New Roman', serif">Georgia</option>
                                            <option value="Verdana, Geneva, sans-serif">Verdana</option>
                                            <option value="Tahoma, Geneva, sans-serif">Tahoma</option>
                                            <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Section 2: Photos */}
                        <div className="border-b border-stone-200">
                            <button
                                onClick={() => setShowPhotos(!showPhotos)}
                                className="w-full flex items-center justify-between p-3 hover:bg-stone-50 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <User className="w-3.5 h-3.5 text-stone-400" />
                                    <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide">Photos</span>
                                </div>
                                {showPhotos ? <ChevronDown className="w-3.5 h-3.5 text-stone-400" /> : <ChevronRight className="w-3.5 h-3.5 text-stone-400" />}
                            </button>
                            {showPhotos && (
                                <div className="px-3 pb-3 space-y-3">
                                    {/* Profile photo */}
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className={LABEL}>Profile Photo</label>
                                            <label className="flex items-center gap-1.5 text-[10px] text-stone-500 cursor-pointer">
                                                <input type="checkbox" checked={useProfilePhoto}
                                                    onChange={e => {
                                                        setUseProfilePhoto(e.target.checked);
                                                        setPhotoUrl(e.target.checked ? (isAdmin ? '{{SenderPhoto}}' : (user?.picture || '')) : '');
                                                    }}
                                                    className="rounded border-stone-300 text-stone-900 focus:ring-stone-500 w-3 h-3" />
                                                {isAdmin ? 'Use sender photo' : 'Use profile photo'}
                                            </label>
                                        </div>
                                        {useProfilePhoto ? (
                                            <div className="px-3 py-2 text-xs text-stone-500 bg-stone-50 rounded-lg border border-stone-200">
                                                {isAdmin
                                                    ? <span>Each user's profile photo <code className="bg-stone-200/60 px-1 py-0.5 rounded text-[10px]">{'{{SenderPhoto}}'}</code></span>
                                                    : <span>Using your profile photo{photoUrl ? '' : ' — upload one in Settings'}</span>}
                                            </div>
                                        ) : (
                                            <input className={`${INPUT} text-xs`} value={photoUrl}
                                                onChange={e => setPhotoUrl(e.target.value)} placeholder="https://..." />
                                        )}
                                    </div>

                                    {/* Photo shape & size */}
                                    {photoUrl && (
                                        <>
                                            <div>
                                                <label className={LABEL}>Photo Shape</label>
                                                <div className="flex gap-1.5">
                                                    {PHOTO_SHAPES.map(s => (
                                                        <button key={s.id} onClick={() => setPhotoShape(s.id)}
                                                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                                                photoShape === s.id ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                                                            }`}>
                                                            <s.icon className="w-3 h-3" /> {s.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <label className={LABEL}>Photo Size ({style.photo_size}px)</label>
                                                <input type="range" min="40" max="150" step="10"
                                                    value={style.photo_size}
                                                    onChange={e => updateStyle('photo_size', e.target.value)}
                                                    className="w-full accent-stone-900" />
                                            </div>
                                        </>
                                    )}

                                    {/* Company logo */}
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className={LABEL}>Company Logo</label>
                                            <label className="flex items-center gap-1.5 text-[10px] text-stone-500 cursor-pointer">
                                                <input type="checkbox" checked={useCompanyLogo}
                                                    onChange={e => {
                                                        setUseCompanyLogo(e.target.checked);
                                                        setLogoUrl(e.target.checked ? (isAdmin ? '{{CompanyLogo}}' : (user?.company_logo_url || '')) : '');
                                                    }}
                                                    className="rounded border-stone-300 text-stone-900 focus:ring-stone-500 w-3 h-3" />
                                                Use company logo
                                            </label>
                                        </div>
                                        {useCompanyLogo ? (
                                            <div className="px-3 py-2 text-xs text-stone-500 bg-stone-50 rounded-lg border border-stone-200">
                                                {isAdmin
                                                    ? <span>Each user's company logo <code className="bg-stone-200/60 px-1 py-0.5 rounded text-[10px]">{'{{CompanyLogo}}'}</code></span>
                                                    : <span>Using your company logo{logoUrl ? '' : ' — upload one in Settings'}</span>}
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <input className={`${INPUT} text-xs flex-1`} value={logoUrl}
                                                    onChange={e => setLogoUrl(e.target.value)} placeholder="https://..." />
                                                <button type="button" onClick={() => setShowLogoPicker(true)}
                                                    className="px-2 py-1.5 text-xs bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-lg transition-colors whitespace-nowrap">
                                                    Library
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Section: Sign-off */}
                        <div className="border-b border-stone-200 px-3 py-3 space-y-2">
                            <label className={LABEL}>Sign-off</label>
                            <div className="flex gap-2">
                                <select
                                    value={SIGN_OFF_OPTIONS.includes(signOff) ? signOff : '__custom__'}
                                    onChange={e => {
                                        if (e.target.value === '__none__') setSignOff('');
                                        else if (e.target.value === '__custom__') setSignOff(signOff || 'Best regards,');
                                        else setSignOff(e.target.value);
                                    }}
                                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-stone-400"
                                >
                                    <option value="__none__">None</option>
                                    {SIGN_OFF_OPTIONS.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                    {signOff && !SIGN_OFF_OPTIONS.includes(signOff) && (
                                        <option value="__custom__">Custom</option>
                                    )}
                                </select>
                            </div>
                            {signOff && !SIGN_OFF_OPTIONS.includes(signOff) && (
                                <input
                                    value={signOff}
                                    onChange={e => setSignOff(e.target.value)}
                                    className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-md focus:outline-none focus:ring-1 focus:ring-stone-400"
                                    placeholder="Custom sign-off..."
                                />
                            )}
                        </div>

                        {/* Section 3: Signature Fields (draggable) */}
                        <div>
                            <button
                                onClick={() => setShowFields(!showFields)}
                                className="w-full flex items-center justify-between p-3 hover:bg-stone-50 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <Type className="w-3.5 h-3.5 text-stone-400" />
                                    <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide">Signature Fields</span>
                                    <span className="text-[10px] text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">{enabledCount}</span>
                                </div>
                                {showFields ? <ChevronDown className="w-3.5 h-3.5 text-stone-400" /> : <ChevronRight className="w-3.5 h-3.5 text-stone-400" />}
                            </button>
                            {showFields && (
                                <div className="px-2 pb-3">
                                    <div className="flex items-center justify-between px-1 mb-1.5">
                                        <p className="text-[10px] text-stone-400">Drag to reorder</p>
                                        <button
                                            onClick={() => {
                                                const allOn = FIELD_DEFS.every(f => enabledFields[f.key]);
                                                const next = {};
                                                FIELD_DEFS.forEach(f => { next[f.key] = !allOn; });
                                                setEnabledFields(next);
                                            }}
                                            className="text-[10px] text-stone-400 hover:text-stone-600"
                                        >
                                            {FIELD_DEFS.every(f => enabledFields[f.key]) ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>

                                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                        <SortableContext items={fieldOrder} strategy={verticalListSortingStrategy}>
                                            {orderedFieldDefs.map(def => (
                                                <SortableFieldRow
                                                    key={def.key}
                                                    def={def}
                                                    enabled={!!enabledFields[def.key]}
                                                    value={fields[def.key]}
                                                    onToggle={() => toggleField(def.key)}
                                                    onChange={v => updateField(def.key, v)}
                                                    isAdmin={isAdmin}
                                                />
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Live Preview (WYSIWYG) */}
                    <div className="p-6 bg-stone-50/50 flex flex-col overflow-y-auto max-h-[700px]">
                        <div className="flex items-center justify-between mb-3 gap-2">
                            <p className="text-[10px] text-stone-400 uppercase tracking-wide font-medium shrink-0">Live Preview</p>
                            {isAdmin && (
                                <div className="flex items-center gap-2">
                                    {/* User preview selector — searchable */}
                                    <UserPreviewSearch
                                        users={previewUsers}
                                        selectedId={previewUserId}
                                        selectedUser={previewUser}
                                        onSelect={(uid) => {
                                            setPreviewUserId(uid);
                                            if (!uid) { setPreviewUser(null); return; }
                                            const found = previewUsers.find(u => u.user_id === uid);
                                            setPreviewUser(found || null);
                                        }}
                                    />
                                    {previewUser ? (
                                        <span className="text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                                            Previewing as {previewUser.name || previewUser.email}
                                        </span>
                                    ) : (
                                        <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                                            Merge variables shown
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex-1 flex items-start">
                            <div className="bg-white rounded-xl border border-stone-200 shadow-sm w-full">
                                {/* Simulated email context */}
                                <div className="px-6 pt-5 pb-3">
                                    <div className="text-sm text-stone-300 leading-relaxed">
                                        <div className="h-2.5 bg-stone-100 rounded w-4/5 mb-2" />
                                        <div className="h-2.5 bg-stone-100 rounded w-3/5 mb-2" />
                                        <div className="h-2.5 bg-stone-100 rounded w-2/5 mb-4" />
                                    </div>
                                </div>
                                {/* Rendered signature */}
                                <div className="px-6 pb-5 border-t border-stone-100 pt-4">
                                    <div
                                        className="signature-preview"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                (previewUser && previewHtmlOutput) ? previewHtmlOutput : htmlOutput,
                                                { ADD_TAGS: ['style'], ADD_ATTR: ['target'] }
                                            )
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Merge variable legend for admin */}
                        {isAdmin && htmlOutput && !previewUser && (
                            <div className="mt-3 p-3 bg-white rounded-lg border border-stone-200">
                                <p className="text-[10px] text-stone-400 uppercase tracking-wide font-medium mb-2">Template Variables</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {FIELD_DEFS.filter(f => enabledFields[f.key] && f.mergeVar).map(f => (
                                        <span key={f.key} className="inline-flex items-center px-2 py-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-md text-[10px] font-mono">
                                            {f.mergeVar}
                                        </span>
                                    ))}
                                    {useProfilePhoto && (
                                        <span className="inline-flex items-center px-2 py-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-md text-[10px] font-mono">
                                            {'{{SenderPhoto}}'}
                                        </span>
                                    )}
                                    {useCompanyLogo && (
                                        <span className="inline-flex items-center px-2 py-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-md text-[10px] font-mono">
                                            {'{{CompanyLogo}}'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showLogoPicker && (
                <ContentPicker
                    open={showLogoPicker}
                    onOpenChange={setShowLogoPicker}
                    onSelect={(asset) => { setLogoUrl(asset.url); setShowLogoPicker(false); }}
                    allowedTypes={['image']}
                    mode="select"
                />
            )}
        </div>
    );
}


// -------------------------------------------------------------------
// Sortable field row (drag handle + checkbox + input)
// -------------------------------------------------------------------
function SortableFieldRow({ def, enabled, value, onToggle, onChange, isAdmin }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: def.key });
    const dragStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto',
    };
    const Icon = def.icon;
    const displayValue = isAdmin && def.mergeVar && enabled && !value ? def.mergeVar : value;
    const placeholder = isAdmin ? (def.mergeVar || def.placeholder) : def.placeholder;

    return (
        <div
            ref={setNodeRef}
            style={dragStyle}
            className={`flex items-center gap-1.5 px-1.5 py-1.5 rounded-lg transition-colors mb-0.5 ${
                enabled ? 'bg-white' : 'bg-stone-50/50'
            } ${isDragging ? 'shadow-lg ring-1 ring-stone-300' : ''}`}
        >
            {/* Drag handle */}
            <button {...attributes} {...listeners} className="flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 text-stone-300 hover:text-stone-500">
                <GripVertical className="w-3.5 h-3.5" />
            </button>

            {/* Checkbox */}
            <button
                onClick={onToggle}
                className={`flex-shrink-0 w-4.5 h-4.5 w-[18px] h-[18px] rounded border-2 flex items-center justify-center transition-all ${
                    enabled ? 'bg-stone-900 border-stone-900 text-white' : 'border-stone-300 hover:border-stone-400'
                }`}
            >
                {enabled && <Check className="w-2.5 h-2.5" />}
            </button>

            {/* Icon */}
            <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${enabled ? 'text-stone-500' : 'text-stone-300'}`} />

            {/* Input or label */}
            {enabled ? (
                <input
                    className="flex-1 min-w-0 px-2 py-1 text-xs border border-stone-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-stone-900/20"
                    value={displayValue}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                />
            ) : (
                <span className="flex-1 text-xs text-stone-400 truncate">{def.label}</span>
            )}
        </div>
    );
}


/* Compact color input */
function ColorInput({ label, value, onChange }) {
    return (
        <div>
            <label className="block text-[10px] font-medium text-stone-400 mb-1">{label}</label>
            <div className="flex items-center gap-1.5">
                <input type="color" value={value} onChange={e => onChange(e.target.value)}
                    className="w-6 h-6 rounded border border-stone-200 cursor-pointer" />
                <input className="flex-1 min-w-0 px-1.5 py-1 text-[10px] border border-stone-200 rounded bg-white focus:outline-none"
                    value={value} onChange={e => onChange(e.target.value)} />
            </div>
        </div>
    );
}
